import { useMemo } from 'react';
import type { FieldValues, UseFormWatch } from 'react-hook-form';

function isEmptyValue(value: unknown): boolean {
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object' && value !== null) return Object.keys(value).length === 0;
  return value === undefined || value === null || value === '';
}

function getValueByPath(data: unknown, path: string): unknown {
  const keys = path.split('.');
  let current = data;

  for (const key of keys) {
    if (current == null || !(key in (current as Record<string, unknown>))) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }

  return current;
}

function areAllRequiredFieldsFilled(data: unknown, requiredKeys: readonly string[]): boolean {
  for (const key of requiredKeys) {
    const value = getValueByPath(data, key);
    if (isEmptyValue(value)) {
      return false;
    }
  }
  return true;
}

function checkConditionalFields(
  values: any,
  conditionalFields: Record<string, Record<string, readonly string[]>>,
  checkOnlyLengthFields: readonly string[]
): boolean {
  for (const conditionKey in conditionalFields) {
    if (!Object.prototype.hasOwnProperty.call(conditionalFields, conditionKey)) continue;

    const conditionValue = getValueByPath(values, conditionKey);
    const branches = conditionalFields[conditionKey];

    for (const branchKey in branches) {
      if (String(conditionValue) === branchKey) {
        const listFields = branches[branchKey];
        for (const listFieldKey of listFields) {
          const listValue = getValueByPath(values, listFieldKey);
          if (Array.isArray(listValue)) {
            if (listValue.length === 0) return false;
          } else if (checkOnlyLengthFields.includes(listFieldKey)) {
            if (!listValue || !Array.isArray(listValue) || listValue.length === 0) return false;
          } else {
            if (isEmptyValue(listValue)) return false;
          }
        }
      }
    }
  }
  return true;
}

function checkAllFieldsFilled<T>(
  values: T,
  skipKeys: readonly string[] = [],
  checkOnlyLengthFields: readonly string[] = [],
  conditionalFields: Record<string, Record<string, readonly string[]>> = {},
  path = '',
  failedFields: string[] = []
): boolean {
  if (typeof values !== 'object' || values == null) {
    if (isEmptyValue(values)) {
      if (path) failedFields.push(path);
      return false;
    }
    return true;
  }

  if (
    Object.keys(conditionalFields).length > 0 &&
    !checkConditionalFields(values, conditionalFields, checkOnlyLengthFields)
  ) {
    return false;
  }

  for (const key in values as Record<string, unknown>) {
    if (!Object.prototype.hasOwnProperty.call(values, key)) continue;
    const value = (values as Record<string, unknown>)[key];
    const currentPath = path ? `${path}.${key}` : key;
    if (skipKeys.includes(currentPath)) continue;

    if (checkOnlyLengthFields.includes(currentPath)) {
      if (!Array.isArray(value) || value.length === 0) {
        failedFields.push(currentPath);
        return false;
      }
      continue;
    }

    if (typeof value === 'object' && value !== null) {
      if (!checkAllFieldsFilled(value, skipKeys, checkOnlyLengthFields, {}, currentPath, failedFields)) {
        return false;
      }
    } else {
      if (isEmptyValue(value)) {
        failedFields.push(currentPath);
        return false;
      }
    }
  }
  return true;
}

export function useFormCompletionStatus<T extends FieldValues>(
  watch: UseFormWatch<T>,
  requiredKeys: readonly string[],
  skipKeysForAllFilled: readonly string[] = [],
  checkOnlyLengthFields: readonly string[] = [],
  conditionalFields: Record<string, Record<string, readonly string[]>> = {},
) {
  const formData = watch();
  const primaryPassenger = { primary_passenger: formData.primary_passenger };

  const isAllRequiredFilled = useMemo(() => {
    return areAllRequiredFieldsFilled(primaryPassenger, requiredKeys);
  }, [primaryPassenger, requiredKeys]);

  const isAllRequiredAndOptionalFilled = useMemo(() => {
    const failedFields: string[] = [];
    const result = checkAllFieldsFilled(
      primaryPassenger,
      skipKeysForAllFilled,
      checkOnlyLengthFields,
      conditionalFields,
      '',
      failedFields
    );
    return result;
  }, [primaryPassenger, skipKeysForAllFilled, checkOnlyLengthFields, conditionalFields]);

  return { isAllRequiredFilled, isAllRequiredAndOptionalFilled };
}